import { useState } from 'react';
import styled, { css } from 'styled-components';
import { MdMail, MdPhone, MdPlace, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import Tooltip from 'components/Tooltip';
import { trim } from 'lodash';
import Modal from 'components/Modal';
import { ModalBody, ModalContent, ModalHeader, ModalTitle } from 'ui/modal';
import { ConnectedRadioGroup, RadioCard } from 'components/RadioCard';
import { reduxForm } from 'redux-form';
import { postPlaceDefaultIdentities } from 'actions/places';
import { getCurrentUser, selectCurrentRole } from 'selectors';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons } from '@tymate/margaret';
import { Avatar, Button, capitalize } from '@tymate/sergic';
import ContractCard from './ContractCard';
import { DetailList, DetailListItem, DetailListItemLink } from 'ui/Details';
import get from 'lodash/get';

export let SERGIC_OFFER = process.env.REACT_APP_SERGIC_OFFER;

const formatAddress = address => (!Boolean(address) ? '' : capitalize(address.full));

const Title = styled.div`
   font-size: ${({ theme }) => theme.size(2)};
   font-weight: 700;
   text-align: center;
   align-items: center;
`;

const Subtitle = styled.p`
   margin-top: 0;
   margin-bottom: 0;
   color: ${({ theme }) => theme.textLight};
`;

const DescriptionDetails = styled.div`
   text-align: left;
   align-items: start;
   line-height: 1.8;

   ${props =>
      props.isCentered &&
      css`
         text-align: center;
         align-items: center;
      `};

   ${props =>
      props.hasTopMargin &&
      css`
         margin-top: ${({ theme }) => theme.spacing()};
      `};
`;

export const DescriptionItem = styled.div`
   display: flex;
   align-items: center;
   color: ${({ theme }) => theme.primary};

   + * {
      padding-top: ${({ theme }) => theme.spacing()};
   }

   > * {
      margin-right: ${({ theme }) => theme.spacing()};
   }
`;

const PrivateWrapper = styled.div`
   color: ${({ theme }) => theme.textLight};
`;

const WrapperIcon = styled.div`
   display: flex;
   align-items: center;
`;

export const IdentityForm = reduxForm({
   form: 'identity',
})(({ identities, handleSubmit, onRequestClose, shallNotBeDismissed, initialValues, children }) => {
   const { t } = useTranslation('profile');

   return (
      <ModalContent>
         <ModalHeader>
            <ModalTitle small>{shallNotBeDismissed ? `${t('modalHello')},` : t('switch')}</ModalTitle>
         </ModalHeader>

         <ModalBody>
            {children}
            <form onSubmit={handleSubmit} initialValues={initialValues}>
               <ConnectedRadioGroup name='selectedIdentity'>
                  {identities.map(({ id, displayName, ...identity }) => (
                     <RadioCard value={id} label={displayName} key={id} avatarUser={{ displayName, ...identity }} />
                  ))}
               </ConnectedRadioGroup>

               <Buttons>
                  {!shallNotBeDismissed && (
                     <Button type='button' onClick={onRequestClose}>
                        {t('misc:cancel')}
                     </Button>
                  )}
                  <Button variant='primary' type='submit'>
                     {t('misc:save')}
                  </Button>
               </Buttons>
            </form>
         </ModalBody>
      </ModalContent>
   );
});

const Profile = ({ user, variant, privacySettings }) => {
   const [identitySwitchModalIsShown, setIdentitySwitchModalIsShown] = useState();
   const selectedPlaceId = useSelector(state => state?.ui?.selectedPlaceId);
   const currentUser = useSelector(getCurrentUser);
   const currentIdentity = get(currentUser, 'currentIdentity', {});
   const { firstName, lastName} = currentIdentity;
   const dispatch = useDispatch();
   const { t } = useTranslation('profile');
   const currentRole = useSelector(selectCurrentRole);

   const handleSubmit = async values => {
      const payload = { identity: { id: values.selectedIdentity } };
      await dispatch(postPlaceDefaultIdentities(selectedPlaceId, payload));
      setIdentitySwitchModalIsShown(false);
   };

   if (!Boolean(user)) {
      return null;
   }

   const isMe = currentUser?.id === user?.id;

   const contracts = user?.contracts;
   const overrideVisibility =
      SERGIC_OFFER === 'VQC' &&
      (currentRole === 'coownership_board_member' || currentRole === 'president_board_member');

   const definitionsDetails = [
      {
         name: t('phone'),
         value: user?.phoneNumber,
         icon: <MdPhone size={24} />,
         visibleOnlytoMe: isMe && privacySettings?.sharePhone === false,
      },
      {
         name: t('email'),
         value: user?.email,
         icon: <MdMail size={24} />,
         visibleOnlytoMe: isMe && privacySettings?.shareEmail === false,
      },
      {
         name: t('address'),
         value: trim(capitalize(formatAddress(user?.address || {}))),
         icon: <MdPlace size={24} />,
         visibleOnlytoMe: isMe && privacySettings?.shareAddress === false,
      },
   ];

   return (
      <>
         <Modal isOpen={identitySwitchModalIsShown} onRequestClose={() => setIdentitySwitchModalIsShown(false)}>
            <IdentityForm
               identities={user?.availableIdentities || []}
               onSubmit={handleSubmit}
               onRequestClose={() => {
                  setIdentitySwitchModalIsShown(false);
               }}
               initialValues={{
                  selectedIdentity: currentUser?.currentIdentity?.id,
               }}
            />
         </Modal>
         <DescriptionDetails isCentered>
            <Avatar hasShadow size='big' firstName={firstName} lastName={lastName} />
            <Title>
               {variant === 'profile'
                  ? capitalize(user?.currentIdentity?.displayName)
                  : (user?.displayPlaceRole || '').indexOf('du conseil syndical') > -1
                  ? capitalize(user?.realName)
                  : capitalize(user?.displayName)}
               {variant !== 'profile' && isMe && ' (Moi)'}
            </Title>
            {(user?.availableIdentities || []).length > 1 && (
               <Buttons alignX='center'>
                  <Button
                     variant='link'
                     onClick={() => {
                        setIdentitySwitchModalIsShown(true);
                     }}
                  >
                     {t('switch')}
                  </Button>
               </Buttons>
            )}
            {variant !== 'profile' && (
               <Subtitle style={{ marginTop: 8 }}>{t(`jobSlang:${user.topPlaceRole}`)}</Subtitle>
            )}
         </DescriptionDetails>
         <DescriptionDetails hasTopMargin>
            {definitionsDetails
               .filter(({ value }) => Boolean(value))
               .map(({ name, value, icon, visibleOnlytoMe }) => (
                  <DescriptionItem key={name}>
                     <WrapperIcon>{icon}</WrapperIcon>
                     <div style={{ flex: 1 }}>{value}</div>
                     <Tooltip left tip={visibleOnlytoMe ? t('private') : t('public')}>
                        <PrivateWrapper>{visibleOnlytoMe ? <MdVisibilityOff /> : <MdVisibility />}</PrivateWrapper>
                     </Tooltip>
                  </DescriptionItem>
               ))}
         </DescriptionDetails>

         {overrideVisibility && contracts && contracts.length > 0 && (
            <Subtitle style={{ marginTop: 8, marginBottom: 8 }}>{t('contracts')}</Subtitle>
         )}
         {overrideVisibility && (
            <DetailList carded>
               {contracts.map(contract => (
                  <DetailListItem key={contract.id}>
                     <DetailListItemLink>
                        <ContractCard contract={contract} readOnly />
                     </DetailListItemLink>
                  </DetailListItem>
               ))}
            </DetailList>
         )}
      </>
   );
};

export default Profile;
